import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    url: `https://91acgn.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '91次元',
      url: 'https://91acgn.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91acgn.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://91acgn.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://91acgn.com/category/${article.category}`, name: article.categoryName } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://91acgn.com/article/${article.id}`, name: article.title } },
    ],
  };

  const pageTitle = `${article.title} - 91次元`;
  const pageDescription = article.excerpt;
  const pageKeywords = article.tags.join(',') + ',91次元';

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://91acgn.com/article/${article.id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://91acgn.com/article/${article.id}`} />
      <meta property="og:image" content={article.coverImage} />
      <meta property="og:site_name" content="91次元" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={article.coverImage} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-[800px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article className="mt-6">
              <header>
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                  {article.categoryName}
                </span>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium text-foreground-700">{article.author}</span>
                  </div>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}阅读</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              <figure className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 91次元`}
                  className="w-full h-auto object-cover"
                />
              </figure>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-foreground-700
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <footer className="mt-10 pt-8 border-t border-background-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="点赞">
                      <i className="ri-thumb-up-line text-lg"></i>
                      <span>{article.likes}</span>
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="评论">
                      <i className="ri-chat-1-line text-lg"></i>
                      <span>{article.comments}</span>
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享">
                      <i className="ri-share-forward-line text-lg"></i>
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 transition-colors cursor-pointer" aria-label="收藏">
                      <i className="ri-bookmark-line text-foreground-600"></i>
                    </button>
                  </div>
                </div>
              </footer>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-14">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-primary-200 transition-all cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img src={rel.coverImage} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </>
  );
}